import { useEffect, useMemo, useRef, useState } from 'react'

import { Heading, Text, theme } from '@hhgtech/hhg-components'
import { ChartOptions } from 'chart.js'
import { Chart as ChartJS } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { gsap } from 'gsap'
import throttle from 'lodash/throttle'
import { useRouter } from 'next/router'
import { Chart } from 'react-chartjs-2'
import { useIntl } from 'react-intl'

import { assetImagePath } from 'interfaces/constants'

import {
  StyledContainer,
  StyledDataContainer,
  StyledMapWrapper,
  StyledPieChart,
  // StyledLabelInfoContainer,
  StyledLegendBox,
  StyledLegendContainer,
  StyledLegendDesktopContainer,
  StyledHeadingContainer,
  StyledPreviousMap,
  StyledNextMap,
  StyledDotsContainer,
  StyledDot,
  StyledPaging,
} from './index.styled'

import 'chart.js/auto'

ChartJS.register(ChartDataLabels)

type MostViewedDataProps = {
  country: string
  locale: string
  bannerImgDesktop: string
  bannerImgMobile: string
  datasets: number[]
}

type Props = {
  isMobile: boolean
  topbarHeight?: number
}

const BlockingAnimationTime = 1000

const MostViewedCategoriesChart = ({ isMobile, topbarHeight = 64 }: Props) => {
  const { formatMessage: f } = useIntl()
  const { locale = 'en-PH' } = useRouter()
  const [hasScrollToView, setHasScrollToView] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const [bannerHeight, setBannerHeight] = useState(isMobile ? 603 : 831)

  const [contentPadding, setContentPadding] = useState(1051)

  const enableSlide = useRef(true)

  const enableWheel = useRef(true)

  const tl = useRef({})
  const indx = useRef({
    index: 0,
  })
  const el = useRef<HTMLDivElement>(null)
  const q = gsap.utils.selector(el)

  const swipeBegin = useRef({
    x: -1,
    y: -1,
  })

  const pieChartLabels = [
    {
      label: f({
        id: 'superContent.mostViewedCategories.labels.cancer',
        defaultMessage: 'Cancer',
      }),
      color: '#2C74DF',
    },
    {
      label: f({
        id: 'superContent.mostViewedCategories.labels.heartHealth',
        defaultMessage: 'Heart Health',
      }),
      color: '#2B94FF',
    },
    {
      label: f({
        id: 'superContent.mostViewedCategories.labels.diabetes',
        defaultMessage: 'Diabetes',
      }),
      color: '#65B5FF',
    },
    {
      label: f({
        id: 'superContent.mostViewedCategories.labels.stroke',
        defaultMessage: 'Stroke',
      }),
      color: '#BCDEFF',
    },
  ]

  // Get the first space if the first word has more than 7 characters, if not get the second
  const indexReplacedSpace = (label: string) =>
    label.indexOf(' ') > 7
      ? label.indexOf(' ')
      : label.indexOf(' ', label.indexOf(' ') + 1)

  const replaceAtIndex = (
    _string: string,
    _index: number,
    _newValue: string,
  ) => {
    if (_index > _string.length - 1 || _index < 0) {
      return _string
    } else {
      return (
        _string.substring(0, _index) + _newValue + _string.substring(_index + 1)
      )
    }
  }

  const mostViewdeData: MostViewedDataProps[] = useMemo(
    () => [
      {
        country: f({
          id: 'superContent.chart.healthTool.items.vietnam',
          defaultMessage: 'Vietnam',
        }),
        locale: 'vi-VN',
        bannerImgDesktop: `${assetImagePath}/obesity-vietnam-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-vietnam-mobile.png?v=1`,
        datasets: [32.2, 41.8, 23.7, 2.2],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.taiwan',
          defaultMessage: 'Taiwan',
        }),
        locale: 'zh-TW',
        bannerImgDesktop: `${assetImagePath}/obesity-taiwan-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-taiwan-mobile.png?v=1`,
        datasets: [32.7, 36.1, 28.7, 2.6],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.thailand',
          defaultMessage: 'Thailand',
        }),
        locale: 'th-TH',
        bannerImgDesktop: `${assetImagePath}/obesity-thailand-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-thailand-mobile.png?v=1`,
        datasets: [46.2, 27.8, 20.4, 5.6],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.philippines',
          defaultMessage: 'Philippines',
        }),
        locale: 'en-PH',
        bannerImgDesktop: `${assetImagePath}/obesity-philippines-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-philippines-mobile.png?v=1`,
        datasets: [58.1, 12.9, 23.2, 5.9],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.malaysia',
          defaultMessage: 'Malaysia',
        }),
        locale: 'ms-MY',
        bannerImgDesktop: `${assetImagePath}/obesity-malaysia-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-malaysia-mobile.png?v=1`,
        datasets: [27.6, 31.5, 40.4, 0.5],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.cambodia',
          defaultMessage: 'Cambodia',
        }),
        locale: 'km-KH',
        bannerImgDesktop: `${assetImagePath}/obesity-cambodia-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-cambodia-mobile.png?v=1`,
        datasets: [36.1, 41.4, 22.4, 0.2],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.indonesia',
          defaultMessage: 'Indonesia',
        }),
        locale: 'id-ID',
        bannerImgDesktop: `${assetImagePath}/obesity-indonesia-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-indonesia-mobile.png?v=1`,
        datasets: [13.1, 26.4, 59.9, 0.6],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.india',
          defaultMessage: 'India',
        }),
        locale: 'hi-IN',
        bannerImgDesktop: `${assetImagePath}/obesity-india-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-india-mobile.png?v=1`,
        datasets: [16.7, 43.3, 34.5, 5.4],
      },
    ],
    [],
  )

  const data = useMemo(
    () => [
      ...mostViewdeData.filter((el) => el.locale === locale),
      ...mostViewdeData.filter((el) => el.locale !== locale),
    ],
    [mostViewdeData],
  )

  const slideDown = () => {
    if (enableSlide) {
      enableSlide.current = false
      setTimeout(() => {
        enableSlide.current = true
      }, BlockingAnimationTime)
      tl.current = gsap
        .timeline()
        .to(q('.animation-image-post'), {
          opacity: 0,
          duration: 1,
        })
        .to(
          q('.animation-image-main'),
          {
            opacity: 1,
            duration: 1,
          },
          0,
        )
        .fromTo(
          q('.country-title'),
          {
            opacity: 0,
            x: -50,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1,
          },
          0,
        )
        .from(
          q('.value-number'),
          {
            textContent: 0,
            snap: { textContent: 0.01 },
            duration: 1,
          },
          0,
        )
    }
  }

  const slideUp = () => {
    if (enableSlide) {
      enableSlide.current = false
      setTimeout(() => {
        enableSlide.current = true
      }, BlockingAnimationTime)

      tl.current = gsap
        .timeline()
        .to(q('.animation-image-main'), {
          opacity: 1,
          duration: 1,
        })
        .to(
          '.animation-image-after',
          {
            opacity: 0,
            duration: 1,
          },
          0,
        )
        .fromTo(
          q('.country-title'),
          {
            opacity: 0,
            x: -50,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1,
          },
          0,
        )
        .from(
          q('.value-number'),
          {
            textContent: 0,
            snap: { textContent: 0.01 },
            duration: 1,
          },
          0,
        )
    }
  }
  useEffect(() => {
    if (indx.current.index <= currentIndex) {
      slideDown()
    } else {
      slideUp()
    }
    indx.current.index = currentIndex
  }, [currentIndex])

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.innerHeight > 0) {
      const bannerHeight = isMobile
        ? window?.innerHeight
        : window?.innerHeight - topbarHeight
      const bannerWidth = (bannerHeight * 1440) / 955
      setBannerHeight(bannerHeight)
      setContentPadding((window.innerWidth - bannerWidth) / 2)
    }
  }, [])

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (!hasScrollToView && typeof window !== 'undefined') {
        const winScroll =
          document.body.scrollTop || document.documentElement.scrollTop

        if (
          winScroll - (el?.current?.offsetTop || 0) + window.innerHeight - 100 >
          0
        ) {
          setHasScrollToView(true)
          window.removeEventListener('scroll', handleScroll)
        }
      }
    }, 200)

    if (
      (el?.current?.offsetTop || 0) <
      (typeof window !== 'undefined' ? window.innerHeight : 1000)
    ) {
      setHasScrollToView(true)
    } else if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (hasScrollToView) {
      tl.current = gsap
        .timeline()
        .fromTo(
          q('.heading'),
          {
            y: 40,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
          },
        )
        .fromTo(
          q('.description'),
          {
            y: 40,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
          },
        )
        .from(
          q('.legend'),
          {
            x: 50,
            duration: 0.8,
            opacity: 0,
          },
          0.5,
        )
    }
  }, [hasScrollToView])

  const currentCountry = data?.[currentIndex]

  const handleWheel = (type: 'next' | 'previous') => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)

      if (type === 'next') {
        typeof window !== 'undefined' &&
          currentIndex < data?.length - 1 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) =>
          v > data?.length - 2 ? data?.length - 1 : v + 1,
        )
      } else {
        typeof window !== 'undefined' &&
          currentIndex > 0 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) => (v < 1 ? 0 : v - 1))
      }
    }
  }

  const handleChangeCountry = (newIndex: number) => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)

      typeof window !== 'undefined' &&
        window.scrollTo({
          top: isMobile
            ? el?.current?.offsetTop
            : (el?.current?.offsetTop || 64) - 64,
          behavior: 'smooth',
        })

      setCurrentIndex(newIndex)
    }
  }

  const handleSlide = (type: 'left' | 'right') => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      if (type === 'left') {
        typeof window !== 'undefined' &&
          currentIndex > 0 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) => (v < 1 ? 0 : v - 1))
      } else {
        typeof window !== 'undefined' &&
          currentIndex < data?.length - 1 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })
        setCurrentIndex((v) =>
          v > data?.length - 2 ? data?.length - 1 : v + 1,
        )
      }

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)
    }
  }

  const configPieChart: ChartOptions = {
    responsive: true,
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
    layout: {
      padding: isMobile ? 60 : 50,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: '#737373',
        backgroundColor: 'white',
        font: {
          weight: 'bold',
          size: 12,
        },
        borderRadius: 12,
        padding: {
          top: 8,
          right: 12,
          bottom: 8,
          left: 12,
        },
        offset: -24,
        textAlign: 'center',
        borderColor: '#E4E8EC',
        borderWidth: 1,
        formatter: (value, context) => {
          return value
            ? `${`${value}`.replace('.', ',')}%\n${replaceAtIndex(
                pieChartLabels[context.dataIndex].label,
                indexReplacedSpace(pieChartLabels[context.dataIndex].label),
                '\n',
              )}`
            : ''
        },
      },
    },
    animation: {
      duration: BlockingAnimationTime,
    },
  }

  return (
    <StyledContainer
      data-banner-height={bannerHeight}
      ref={el}
      onTouchStart={(e) => {
        swipeBegin.current.x = e.changedTouches[0].clientX
        swipeBegin.current.y = e.changedTouches[0].clientY
      }}
      onTouchEnd={(e) => {
        if (
          swipeBegin.current.x > 0 &&
          Math.abs(e.changedTouches[0].clientX - swipeBegin.current.x) >
            Math.abs(e.changedTouches[0].clientY - swipeBegin.current.y)
        ) {
          handleSlide(
            e.changedTouches[0].clientX - swipeBegin.current.x > 0
              ? 'left'
              : 'right',
          )
        }
        swipeBegin.current.x = -1
        swipeBegin.current.y = -1
      }}
    >
      {isMobile ? (
        <>
          <Heading
            className="heading"
            tag="h2"
            color={theme.colors.primaryBase}
          >
            {f({
              id: 'superContent.mostViewedCategories.title',
              defaultMessage: 'Most viewed categories on Hello Health Sites:',
            })}
          </Heading>
          <Text
            size="p4"
            className="description"
            color={theme.colors.gray500}
            style={{ marginTop: 12 }}
          >
            {f({
              id: 'superContent.mostViewedCategories.description',
              defaultMessage:
                'Based on interactions with Hello Health Sites from 26 May 2021 to 25 August 2021',
            })}
          </Text>
          <StyledMapWrapper>
            {data?.map((data, index) => (
              <img
                key={index}
                loading="lazy"
                className={`animation animation-image${
                  index < currentIndex ? ' animation-image-post' : ''
                }${currentIndex === index ? ' animation-image-main' : ''}${
                  currentIndex < index ? ' animation-image-after' : ''
                }`}
                src={isMobile ? data?.bannerImgMobile : data?.bannerImgDesktop}
                alt={f({
                  id: 'superContent.mostViewedCategories.img.alt',
                  defaultMessage:
                    'Most viewed categories on Hello Health Sites:',
                })}
              />
            ))}
            <StyledDataContainer data-content-padding={contentPadding}>
              <div className="country-title">{currentCountry?.country}</div>
              <StyledPieChart>
                <Chart
                  type="pie"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  options={configPieChart as any}
                  data={{
                    labels: pieChartLabels.map((e) => e.label),
                    datasets: [
                      {
                        data: currentCountry?.datasets,
                        backgroundColor: pieChartLabels.map((e) => e.color),
                      },
                    ],
                  }}
                />
              </StyledPieChart>
            </StyledDataContainer>
          </StyledMapWrapper>
          <StyledLegendContainer>
            {pieChartLabels.map((label, index) => (
              <div className="legend" key={index}>
                <StyledLegendBox style={{ backgroundColor: label.color }} />
                <div className="legend-label">{label.label}</div>
              </div>
            ))}
          </StyledLegendContainer>
          <StyledDotsContainer>
            {data?.map((e, index) => (
              <StyledDot
                key={`${e?.country}-${index}`}
                data-active={index === currentIndex}
                onClick={() => handleChangeCountry(index)}
              ></StyledDot>
            ))}
          </StyledDotsContainer>
        </>
      ) : (
        <>
          {data?.map((data, index) => (
            <img
              key={index}
              loading="lazy"
              className={`animation animation-image${
                index < currentIndex ? ' animation-image-post' : ''
              }${currentIndex === index ? ' animation-image-main' : ''}${
                currentIndex < index ? ' animation-image-after' : ''
              }`}
              src={isMobile ? data?.bannerImgMobile : data?.bannerImgDesktop}
              alt={f({
                id: 'superContent.mostViewedCategories.img.alt',
                defaultMessage: 'Most viewed categories on Hello Health Sites:',
              })}
            />
          ))}
          <StyledHeadingContainer data-content-padding={contentPadding}>
            <Heading
              className="heading"
              tag="h2"
              color={theme.colors.primaryBase}
            >
              {f({
                id: 'superContent.mostViewedCategories.title',
                defaultMessage: 'Most viewed categories on Hello Health Sites:',
              })}
            </Heading>
            <Text
              size="p4"
              className="description"
              color={theme.colors.gray500}
            >
              {f({
                id: 'superContent.mostViewedCategories.description',
                defaultMessage:
                  'Based on interactions with Hello Health Sites from 26 May 2021 to 25 August 2021',
              })}
            </Text>
          </StyledHeadingContainer>
          <StyledDataContainer data-content-padding={contentPadding}>
            <div className="country-title">{currentCountry?.country}</div>
            <StyledPieChart>
              <Chart
                type="pie"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                options={configPieChart as any}
                data={{
                  labels: pieChartLabels.map((e) => e.label),
                  datasets: [
                    {
                      data: currentCountry?.datasets,
                      backgroundColor: pieChartLabels.map((e) => e.color),
                    },
                  ],
                }}
              />
            </StyledPieChart>
          </StyledDataContainer>
          <StyledLegendDesktopContainer data-content-padding={contentPadding}>
            <StyledLegendContainer>
              {pieChartLabels.map((label, index) => (
                <div className="legend" key={index}>
                  <StyledLegendBox style={{ backgroundColor: label.color }} />
                  <div className="legend-label">{label.label}</div>
                </div>
              ))}
            </StyledLegendContainer>
          </StyledLegendDesktopContainer>
          {currentIndex !== 0 ? (
            <StyledPreviousMap>
              <img
                loading="lazy"
                src={`${assetImagePath}/chevronLeftCircle.svg`}
                alt="previous"
                onClick={() => handleWheel('previous')}
              />
            </StyledPreviousMap>
          ) : null}
          {currentIndex < data?.length - 1 ? (
            <StyledNextMap>
              <img
                loading="lazy"
                src={`${assetImagePath}/chevronRightCircle.svg`}
                alt="next"
                onClick={() => handleWheel('next')}
              />
            </StyledNextMap>
          ) : null}
          <StyledPaging data-content-padding={contentPadding}>
            <b>{currentIndex + 1}</b>/{data.length}
          </StyledPaging>
        </>
      )}
    </StyledContainer>
  )
}

export { MostViewedCategoriesChart }
